import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { sentenceCaseLabel, useTranslation } from "../i18n";
import { countryCodes } from "../data/countryCodes";
import { backendUrl } from "../Router/constant";
import {
  completeVendorProfileAction,
  previewVendorProfileAction,
  uploadVendorDocumentAction,
} from "../actions/auth";
import { AUTH_ME } from "../actions/type";
import "./CompleteProfile.css";

const DRAFT_KEY = "vendorProfileCompletionDraft";
const STEPS = [
  "Company details",
  "Documents",
  "Review",
  "AI Scanning",
  "Success",
];
const PROGRESS_STEP_BY_FLOW_STEP = [0, 1, 1, 2, 3, 4];
const FLOW_STEP_BY_PROGRESS_STEP = [0, 1, 3, 4, 5];
const getProgressStep = (flowStep, verificationStatus) => {
  if (
    flowStep === 4 &&
    ["passed", "failed"].includes(verificationStatus)
  ) {
    return 4;
  }
  return PROGRESS_STEP_BY_FLOW_STEP[flowStep] ?? STEPS.length - 1;
};
const DOCS = [
  {
    key: "registrationCertificate",
    title: "Company Registration Certificate",
    detail:
      "Upload your certificate of incorporation or registration document.",
    required: true,
    tone: "blue",
  },
  {
    key: "taxCertificate",
    title: "Tax/VAT Certificate",
    detail: "Upload your tax registration or VAT certificate.",
    required: true,
    tone: "green",
  },
  {
    key: "businessLicense",
    title: "Business License",
    detail: "Upload your valid business license, if applicable.",
    required: true,
    tone: "purple",
  },
  {
    key: "otherCertificates",
    title: "Other Certificates",
    detail: "Upload any other relevant certificates or documents.",
    required: false,
    multiple: true,
    tone: "orange",
  },
];
const COMPANY_TYPES = [
  "Private Limited",
  "Private Limited Company",
  "Public Limited",
  "Public Limited Company",
  "Limited Liability Company",
  "Partnership",
  "Sole Proprietorship",
  "Trader",
  "Manufacturer",
  "Other",
];
const EMPTY_FORM = {
  companyLegalName: "",
  tradingName: "",
  registrationNumber: "",
  countryOfRegistration: "",
  companyType: "",
};
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const getEmptyDocument = () => ({
  file: null,
  name: "",
  size: 0,
  uploadId: "",
});
const hydrateDocuments = (savedDocuments = {}) =>
  Object.fromEntries(
    DOCS.map(({ key, multiple }) => {
      const saved = savedDocuments[key];
      if (multiple) {
        const savedList = Array.isArray(saved)
          ? saved
          : saved?.name || saved?.uploadId
            ? [saved]
            : [];
        return [key, savedList.map((document) => ({ ...getEmptyDocument(), ...document, file: null }))];
      }
      return [key, { ...getEmptyDocument(), ...(saved || {}), file: null }];
    }),
  );
const serializeDocuments = (documents = {}) =>
  Object.fromEntries(
    DOCS.map(({ key, multiple }) => {
      const serialize = (document = {}) => ({
        name: document.name || "",
        uploadId: document.uploadId || "",
        size: document.size || 0,
      });
      return [
        key,
        multiple
          ? (documents[key] || []).map(serialize)
          : serialize(documents[key]),
      ];
    }),
  );
const getDocumentIds = (documents = {}) =>
  Object.fromEntries(
    DOCS.map(({ key, multiple }) => [
      key,
      multiple
        ? (documents[key] || []).map(({ uploadId }) => uploadId).filter(Boolean)
        : documents[key]?.uploadId || "",
    ]),
  );
const documentKeyForApiField = (field = "") => {
  const value = String(field).toLowerCase().replace(/[^a-z]/g, "");
  if (value.includes("othercertificate")) return "otherCertificates";
  if (value.includes("businesslicense")) return "businessLicense";
  if (value.includes("taxvat") || value.includes("taxcertificate"))
    return "taxCertificate";
  if (value.includes("registration") || value.includes("incorporation"))
    return "registrationCertificate";
  return "";
};
const documentKeyForApiMessage = (message = "") =>
  documentKeyForApiField(message);
const isFailedVerificationEntry = (entry = {}) => {
  const resultStatus = entry.status || entry.result || entry.verificationResult;
  return (
    /^(failed|failure|rejected|error|invalid|mismatch|expired|not[_ -]?verified|verification[_ -]?failed)$/i.test(
      String(resultStatus || "").trim(),
    ) ||
    entry.success === false ||
    entry.verified === false ||
    entry.isValid === false ||
    entry.verificationResult === false
  );
};

const getProfileIdentity = (profile = {}) => {
  const identity =
    profile._id ||
    profile.id ||
    profile.vendorId?._id ||
    profile.vendorId?.id ||
    profile.vendorId ||
    profile.vendor?._id ||
    profile.vendor?.id ||
    profile.username ||
    profile.email;
  return identity === undefined || identity === null
    ? ""
    : String(identity).trim().toLowerCase();
};
const readDraft = (storageKey) => {
  if (!storageKey) return {};
  try {
    return JSON.parse(localStorage.getItem(storageKey) || "{}") || {};
  } catch {
    return {};
  }
};
const profileCompletionStep = (percentage, profile = {}) => {
  const hasPercentage =
    percentage !== undefined && percentage !== null && percentage !== "";
  const numericPercentage = hasPercentage ? Number(percentage) : null;
  if (numericPercentage === 100) return 5;
  if (numericPercentage === 0) return 0;

  const company = profile.companyProfile || profile.company || {};
  const hasCompanyDetails = [
    company.companyLegalName ||
      company.legalName ||
      company.companyName ||
      profile.companyLegalName ||
      profile.companyName,
    company.tradingName || profile.tradingName,
    company.registrationNumber ||
      company.registrationNo ||
      profile.registrationNumber,
    company.countryOfRegistration || profile.countryOfRegistration,
    company.companyType ||
      company.companyTypeName ||
      profile.companyType ||
      profile.companyTypeName,
  ].every((value) => String(value || "").trim());
  if (!hasCompanyDetails) return 0;

  const documents = company.documents || profile.documents || {};
  const documentHasId = (value) => {
    if (typeof value === "string" || typeof value === "number")
      return Boolean(String(value).trim());
    return Boolean(
      value &&
      (value._id ||
        value.id ||
        value.uploadId ||
        value.fileId ||
        value.documentId ||
        value.url),
    );
  };
  const hasDocument = (aliases, keywords) => {
    const directValue = aliases
      .map((name) => company[name] || profile[name])
      .find(Boolean);
    if (documentHasId(directValue)) return true;
    const records = Array.isArray(documents)
      ? documents
      : Object.entries(documents).map(([name, value]) => ({
          name,
          ...(value && typeof value === "object" ? value : { id: value }),
        }));
    return records.some((document) => {
      const label = [
        document.name,
        document.label,
        document.type,
        document.documentType,
        document.key,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return (
        keywords.some((keyword) => label.includes(keyword)) &&
        documentHasId(document)
      );
    });
  };
  const hasRequiredDocuments =
    hasDocument(
      [
        "companyRegistrationCertificate",
        "registrationCertificate",
        "registrationCertificateId",
      ],
      ["registration", "incorporation"],
    ) &&
    hasDocument(
      ["taxVatCertificate", "taxCertificate", "taxCertificateId"],
      ["tax", "vat"],
    ) &&
    hasDocument(
      ["businessLicense", "businessLicenseId", "license"],
      ["business license", "business_license", "license"],
    );
  return hasRequiredDocuments ? 2 : 1;
};
const iconPaths = {
  building: (
    <>
      <path d="M4 20h16M6 20V5.5L12 3v17M12 8h6v12M8.5 7.5h1M8.5 11h1M8.5 14.5h1M14.5 11h1M14.5 14.5h1" />
    </>
  ),
  document: (
    <>
      <path d="M6 3.5h8l4 4V20H6zM14 3.5v4h4M9 12h6M9 15.5h6" />
    </>
  ),
  upload: <path d="M12 15V4m0 0L8 8m4-4 4 4M5 15v4h14v-4" />,
  review: (
    <>
      <path d="M6 3.5h8l4 4V20H6zM14 3.5v4h4M9 12h6M9 15.5h3" />
      <path d="m14 16 1.5 1.5L19 14" />
    </>
  ),
  scan: (
    <>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="m15.5 15.5 5 5M8 10.5h5M10.5 8v5" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path
        d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-1.7 2.94-.08-.02a1.7 1.7 0 0 0-1.78.5l-.06.07h-3.4l-.02-.08a1.7 1.7 0 0 0-1.28-1.26l-.08-.02-1.7-2.94.06-.06A1.7 1.7 0 0 0 10.1 14l-.02-.08v-3.4l.08-.02a1.7 1.7 0 0 0 1.26-1.28l.02-.08 2.94-1.7.06.06a1.7 1.7 0 0 0 1.88.34l.08-.02 2.94 1.7-.02.08a1.7 1.7 0 0 0 .5 1.78l.07.06v3.4l-.08.02A1.7 1.7 0 0 0 19.4 15Z"
        transform="translate(-2 -2) scale(1.17)"
      />
    </>
  ),
  shield: (
    <>
      <path d="M12 3 19 6v5c0 4.5-2.8 7.8-7 10-4.2-2.2-7-5.5-7-10V6z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  arrow: <path d="M4 12h15m-6-6 6 6-6 6" />,
  back: <path d="m15 18-6-6 6-6M9 12h11" />,
  chevronDown: <path d="m6 9 6 6 6-6" />,
  check: <path d="m5 12 4.5 4.5L19 7" />,
  edit: <path d="m14 5 5 5M4 20l4.4-.9L19 8.5 15.5 5 4.9 15.6z" />,
  eye: (
    <>
      <path d="M2.5 12s3.3-6 9.5-6 9.5 6 9.5 6-3.3 6-9.5 6-9.5-6-9.5-6Z" />
      <circle cx="12" cy="12" r="2.5" />
    </>
  ),
};
function Icon({ name, className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {iconPaths[name] || iconPaths.document}
    </svg>
  );
}
function StepProgress({
  step,
  text,
  onStepClick,
  canNavigateTo,
  steps = STEPS,
  completeCurrentStep = false,
}) {
  return (
    <section
      className="complete-profile-progress-card"
      aria-label={text("Profile completion steps")}
    >
      <div className="complete-profile-step-list">
        {steps.map((label, index) => {
          const canNavigate =
            Boolean(onStepClick) &&
            index < step &&
            (!canNavigateTo || canNavigateTo(index));
          const isComplete =
            index < step || (completeCurrentStep && index === step);
          const isCurrent = index === step && !completeCurrentStep;
          return (
            <div className="complete-profile-step-item" key={label}>
              <button
                type="button"
                className={
                  "complete-profile-step" +
                  (isComplete ? " is-complete" : "") +
                  (isCurrent ? " is-current" : "")
                }
                aria-current={isCurrent ? "step" : undefined}
                aria-label={
                  canNavigate ? text("Go back to") + " " + text(label) : undefined
                }
                disabled={!canNavigate}
                onClick={() => onStepClick?.(index)}
              >
                <span className="complete-profile-step-marker">
                  {isComplete ? <Icon name="check" /> : index + 1}
                </span>
                <span className="complete-profile-step-copy">
                  <span className="complete-profile-step-label">
                    {text(label)}
                  </span>
                  <small className="complete-profile-step-status">
                    {text(
                      isComplete
                        ? "Completed"
                        : isCurrent
                          ? "In progress"
                          : "Not started",
                    )}
                  </small>
                </span>
              </button>
              {index < steps.length - 1 && (
                <span
                  className="complete-profile-step-connector"
                  aria-hidden="true"
                />
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
function CompletionSummary({ step, text }) {
  const progress = Math.round(((step + 1) / STEPS.length) * 100);
  const completedSections = step + 1;
  return (
    <div className="complete-profile-completion-summary">
      <div
        className="complete-profile-progress-ring"
        role="progressbar"
        aria-label={text("Profile completion")}
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow={progress}
        style={{ "--progress": progress + "%" }}
      >
        <span>{progress}%</span>
      </div>
      <div className="complete-profile-completion-copy">
        <strong>{text("Form completed")}</strong>
        <span>
          {completedSections} {text("of")} {STEPS.length}{" "}
          {text("sections complete")}
        </span>
      </div>
    </div>
  );
}
function SectionTitle({ icon, title, description, tone = "amber", action }) {
  return (
    <div className="complete-profile-section-title">
      <span className={`complete-profile-section-icon ${tone}`}>
        <Icon name={icon} />
      </span>
      <div className="complete-profile-section-copy">
        <h2>{sentenceCaseLabel(title)}</h2>
        <p>{description}</p>
      </div>
      {action}
    </div>
  );
}
function Field({
  label,
  value,
  onChange,
  placeholder,
  required,
  options,
  error,
}) {
  const fieldId =
    "complete-profile-" + label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <div className="complete-profile-field">
      <label
        className="complete-profile-field-label"
        id={fieldId + "-label"}
        htmlFor={fieldId}
      >
        {label}
        {required && <b> *</b>}
      </label>
      {options ? (
        <FieldDropdown
          id={fieldId}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          options={options}
        />
      ) : (
        <input
          id={fieldId}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
        />
      )}
      {error && <span className="complete-profile-field-error">{error}</span>}
    </div>
  );
}
function FieldDropdown({
  id,
  value,
  onChange,
  placeholder,
  required,
  options,
}) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const selectedOption = options.find((option) => option.value === value);
  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (!dropdownRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);
  useEffect(() => {
    if (open && menuRef.current) menuRef.current.scrollTop = 0;
  }, [open]);
  return (
    <div
      className={"complete-profile-dropdown" + (open ? " is-open" : "")}
      ref={dropdownRef}
    >
      <button
        id={id}
        type="button"
        className={
          "complete-profile-dropdown-trigger" +
          (selectedOption ? "" : " is-placeholder")
        }
        aria-labelledby={id + "-label"}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-required={required}
        aria-controls={id + "-options"}
        onClick={() => setOpen((visible) => !visible)}
      >
        {selectedOption?.flag && (
          <img
            className="complete-profile-country-flag"
            src={selectedOption.flag}
            alt=""
            aria-hidden="true"
          />
        )}
        <span className="complete-profile-dropdown-value">
          {selectedOption?.label || placeholder}
        </span>
        <Icon
          name="chevronDown"
          className="complete-profile-dropdown-chevron"
        />
      </button>
      {open && (
        <div
          className="complete-profile-dropdown-menu"
          id={id + "-options"}
          role="listbox"
          aria-labelledby={id + "-label"}
          ref={menuRef}
        >
          {options.map((option) => (
            <button
              type="button"
              role="option"
              key={option.value}
              aria-selected={option.value === value}
              className={
                "complete-profile-dropdown-option" +
                (option.value === value ? " is-selected" : "")
              }
              onClick={() => {
                onChange({ target: { value: option.value } });
                setOpen(false);
              }}
            >
              {option.flag && (
                <img
                  className="complete-profile-country-flag"
                  src={option.flag}
                  alt=""
                  aria-hidden="true"
                />
              )}
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
function ReviewField({ label, value }) {
  return (
    <div className="complete-profile-review-field">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
function SuccessCard({ icon, title, description, tone = "green" }) {
  return (
    <article className="complete-profile-success-card">
      <span className={`complete-profile-section-icon ${tone}`}>
        <Icon name={icon} />
      </span>
      <div>
        <h3>{sentenceCaseLabel(title)}</h3>
        <p>{description}</p>
      </div>
      <span className="complete-profile-success-badge">
        <Icon name="check" />
      </span>
    </article>
  );
}
function NextAction({ icon, title, description, onClick, tone = "amber" }) {
  return (
    <button
      type="button"
      className="complete-profile-next-action"
      onClick={onClick}
    >
      <span className={`complete-profile-section-icon ${tone}`}>
        <Icon name={icon} />
      </span>
      <span className="complete-profile-next-copy">
        <strong>{title}</strong>
        <small>{description}</small>
      </span>
      <Icon name="arrow" className="complete-profile-next-arrow" />
    </button>
  );
}
function CompleteProfile() {
  const { text } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authMe = useSelector((state) => state.auth?.authMe || {});
  const dashboardData = useSelector(
    (state) => state.vendorDashboard?.data || {},
  );
  const profileCompletionPercentage =
    dashboardData.profileCompletionPercentage ??
    authMe.profileCompletionPercentage ??
    authMe.companyProfile?.profileCompletionPercentage;
  const profileStep = profileCompletionStep(
    profileCompletionPercentage,
    authMe,
  );
  const profileIdentity = getProfileIdentity(authMe);
  const draftStorageKey = profileIdentity
    ? DRAFT_KEY + ":" + encodeURIComponent(profileIdentity)
    : "";
  const initialDraft = useMemo(
    () => readDraft(draftStorageKey),
    [draftStorageKey],
  );
  const [currentStep, setCurrentStep] = useState(profileStep);
  const [form, setForm] = useState(() => {
    const company = authMe.companyProfile || {};
    return {
      ...EMPTY_FORM,
      companyLegalName:
        company.companyLegalName ||
        company.legalName ||
        company.companyName ||
        authMe.companyLegalName ||
        authMe.companyName ||
        "",
      tradingName: company.tradingName || authMe.tradingName || "",
      registrationNumber:
        company.registrationNumber ||
        company.registrationNo ||
        authMe.registrationNumber ||
        "",
      countryOfRegistration:
        company.countryOfRegistration || authMe.countryOfRegistration || "",
      companyType:
        company.companyType ||
        company.companyTypeName ||
        authMe.companyType ||
        authMe.companyTypeName ||
        "",
      ...(initialDraft.form || {}),
    };
  });
  const [documents, setDocuments] = useState(() =>
    hydrateDocuments(initialDraft.documents),
  );
  const [formErrors, setFormErrors] = useState({});
  const [documentErrors, setDocumentErrors] = useState({});
  const [draftMessage, setDraftMessage] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionStatus, setExtractionStatus] = useState("idle");
  const [extractionFileIndex, setExtractionFileIndex] = useState(0);
  const [extractedDocuments, setExtractedDocuments] = useState([]);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanStatus, setScanStatus] = useState("idle");
  const [scanResults, setScanResults] = useState([]);
  const [failedDocuments, setFailedDocuments] = useState([]);
  const [verifiedVendor, setVerifiedVendor] = useState(null);
  const [scanError, setScanError] = useState("");
  const [replacementTarget, setReplacementTarget] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [isCompletedProfilePreview, setIsCompletedProfilePreview] =
    useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileSubmitError, setProfileSubmitError] = useState("");
  const hasStartedVerificationFlow = useRef(false);
  const hasStartedPreviewRequest = useRef(false);
  const currentVerificationEntries = failedDocuments.length
    ? failedDocuments
    : scanResults.filter(isFailedVerificationEntry);
  const failedVerificationEntries = currentVerificationEntries.filter(
    (entry) => {
      const failure = failedDocuments.find(
        (item) =>
          item.field === entry.field &&
          (item.index === undefined || item.index === entry.index) &&
          (!item.uploadId || item.uploadId === entry.uploadId),
      );
      return (
        isFailedVerificationEntry(entry) ||
        isFailedVerificationEntry(failure || {}) ||
        Boolean(failure)
      );
    },
  );
  const getAdminApprovalStatus = (entry, relatedFailure) =>
    String(
      relatedFailure?.adminApprovalStatus ||
        entry?.adminApprovalStatus ||
        relatedFailure?.approvalStatus ||
        entry?.approvalStatus ||
        "",
    ).toLowerCase();
  const allFailedDocumentsApproved =
    failedVerificationEntries.length > 0 &&
    failedVerificationEntries.every((entry) => {
      const failure = failedDocuments.find(
        (item) =>
          item.field === entry.field &&
          (item.index === undefined || item.index === entry.index) &&
          (!item.uploadId || item.uploadId === entry.uploadId),
      );
      return getAdminApprovalStatus(entry, failure) === "approved";
    });
  const needsAdminApproval =
    failedVerificationEntries.length > 0 && !allFailedDocumentsApproved;
  useEffect(() => {
    const company = authMe.companyProfile || {};
    const profileDefaults = {
      companyLegalName:
        company.companyLegalName ||
        company.legalName ||
        company.companyName ||
        authMe.companyLegalName ||
        authMe.companyName,
      tradingName: company.tradingName || authMe.tradingName,
      registrationNumber:
        company.registrationNumber ||
        company.registrationNo ||
        authMe.registrationNumber,
      countryOfRegistration:
        company.countryOfRegistration || authMe.countryOfRegistration,
      companyType:
        company.companyType ||
        company.companyTypeName ||
        authMe.companyType ||
        authMe.companyTypeName,
    };
    setForm((value) => {
      const next = { ...value };
      let changed = false;
      Object.entries(profileDefaults).forEach(([key, profileValue]) => {
        if (!value[key] && profileValue) {
          next[key] = profileValue;
          changed = true;
        }
      });
      return changed ? next : value;
    });
  }, [authMe]);

  useEffect(() => {
    if (hasStartedVerificationFlow.current) return;
    setCurrentStep((step) => (step === 5 ? 5 : profileStep));
  }, [profileStep]);

  useEffect(() => {
    if (!draftStorageKey) return;
    const draft = readDraft(draftStorageKey);
    if (draft.form) {
      const company = authMe.companyProfile || {};
      const profileValues = {
        companyLegalName:
          company.companyLegalName ||
          company.legalName ||
          company.companyName ||
          authMe.companyLegalName ||
          authMe.companyName ||
          "",
        tradingName: company.tradingName || authMe.tradingName || "",
        registrationNumber:
          company.registrationNumber ||
          company.registrationNo ||
          authMe.registrationNumber ||
          "",
        countryOfRegistration:
          company.countryOfRegistration || authMe.countryOfRegistration || "",
        companyType:
          company.companyType ||
          company.companyTypeName ||
          authMe.companyType ||
          authMe.companyTypeName ||
          "",
      };
      setForm((value) => ({
        ...value,
        ...draft.form,
        ...Object.fromEntries(
          Object.entries(profileValues).filter(([, fieldValue]) => fieldValue),
        ),
      }));
    }
    if (draft.documents) {
      setDocuments(hydrateDocuments(draft.documents));
    }
  }, [authMe, draftStorageKey]);

  const countryOptions = useMemo(
    () =>
      countryCodes
        .filter((country) => country?.name && country?.code)
        .map((country) => ({
          value: country.name,
          label: country.name,
          flag:
            "https://flagcdn.com/24x18/" +
            country.code.toLowerCase() +
            ".png",
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [],
  );
  const companyTypeOptions = COMPANY_TYPES.map((type) => ({
    value: type,
    label: text(type),
  }));
  const saveDraft = useCallback(
    (step = currentStep, nextForm = form, nextDocuments = documents) => {
      const savedDocuments = serializeDocuments(nextDocuments);
      try {
        if (!draftStorageKey) return;
        localStorage.setItem(
          draftStorageKey,
          JSON.stringify({ step, form: nextForm, documents: savedDocuments }),
        );
        setDraftMessage(text("Draft saved on this device."));
        window.setTimeout(() => setDraftMessage(""), 3200);
      } catch {
        setDraftMessage(text("Unable to save draft on this device."));
      }
    },
    [currentStep, documents, draftStorageKey, form, text],
  );
  const updateField = (key) => (event) => {
    setForm((value) => ({ ...value, [key]: event.target.value }));
    setFormErrors((value) => ({ ...value, [key]: "" }));
    setScanError("");
  };
  const canContinueCompany = [
    form.companyLegalName,
    form.tradingName,
    form.registrationNumber,
    form.countryOfRegistration,
    form.companyType,
  ].every((value) => String(value || "").trim());

  const handleCompanySubmit = (event) => {
    event.preventDefault();
    const requiredFields = [
      ["companyLegalName", "Enter the legal company name."],
      ["tradingName", "Enter the trading name."],
      ["registrationNumber", "Enter the registration number."],
      ["countryOfRegistration", "Select a country."],
      ["companyType", "Select a company type."],
    ];
    const errors = Object.fromEntries(
      requiredFields
        .filter(([key]) => !String(form[key] || "").trim())
        .map(([key, message]) => [key, text(message)]),
    );
    setFormErrors(errors);
    if (Object.keys(errors).length) return;
    setCurrentStep(1);
    saveDraft(1);
    setDraftMessage("");
  };
  const setSelectedFiles = (key, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const invalidFile = files.find((file) => {
      const validType =
        /\.pdf$/i.test(file.name) &&
        ["", "application/pdf", "application/octet-stream"].includes(file.type);
      return !validType || file.size > MAX_FILE_SIZE;
    });
    if (invalidFile) {
      const validType =
        /\.pdf$/i.test(invalidFile.name) &&
        ["", "application/pdf", "application/octet-stream"].includes(invalidFile.type);
      return setDocumentErrors((value) => ({
        ...value,
        [key]: validType
          ? text("Files must be 10 MB or smaller.")
          : text("Choose a PDF file."),
      }));
    }

    const documentConfig = DOCS.find((document) => document.key === key);
    if (documentConfig?.multiple) {
      setDocuments((value) => {
        const next = [...(value[key] || [])];
        const targetIndex =
          replacementTarget?.key === key ? replacementTarget.index : null;
        const newDocuments = files.map((file) => ({
          file,
          name: file.name,
          size: file.size,
          uploadId: "",
        }));
        if (Number.isInteger(targetIndex) && targetIndex >= 0 && targetIndex < next.length) {
          next[targetIndex] = newDocuments[0];
          next.push(...newDocuments.slice(1));
        } else {
          next.push(...newDocuments);
        }
        return { ...value, [key]: next };
      });
    } else {
      const file = files[0];
      setDocuments((value) => ({
        ...value,
        [key]: { file, name: file.name, size: file.size, uploadId: "" },
      }));
    }
    setReplacementTarget(null);
    setDocumentErrors((value) => ({ ...value, [key]: "" }));
    setExtractedDocuments([]);
    setExtractionStatus("idle");
    hasStartedPreviewRequest.current = false;
    setScanStatus("idle");
    setFailedDocuments([]);
    setScanError("");
  };
  const handleDrop = (event, key) => {
    event.preventDefault();
    setSelectedFiles(key, event.dataTransfer.files);
  };
  const requiredDocumentsReady = DOCS.filter((document) => document.required).every(
    ({ key, multiple }) =>
      multiple
        ? (documents[key] || []).some((document) => document.file || document.uploadId)
        : Boolean(documents[key]?.file || documents[key]?.uploadId),
  );

  const buildProfilePayload = useCallback(
    (sourceDocuments = documents) => {
      const ids = getDocumentIds(sourceDocuments);
      return {
        companyLegalName: form.companyLegalName,
        tradingName: form.tradingName,
        registrationNumber: form.registrationNumber,
        countryOfRegistration: form.countryOfRegistration,
        companyType: form.companyType,
        companyTypeName: form.companyType,
        companyRegistrationCertificate: ids.registrationCertificate,
        taxVatCertificate: ids.taxCertificate,
        businessLicense: ids.businessLicense,
        otherCertificates: ids.otherCertificates,
      };
    },
    [documents, form],
  );

  const runProfilePreview = useCallback(async (documentsToPreview = documents) => {
    if (isExtracting || hasStartedPreviewRequest.current) return;
    hasStartedPreviewRequest.current = true;
    hasStartedVerificationFlow.current = true;
    setCurrentStep(2);
    setExtractionStatus("processing");
    setExtractionFileIndex(0);
    setScanProgress(4);
    setExtractedDocuments([]);
    setScanError("");
    setIsExtracting(true);

    const uploadedFileCount = DOCS.reduce((count, config) => {
      const entries = config.multiple
        ? documentsToPreview[config.key] || []
        : [documentsToPreview[config.key]].filter(Boolean);
      return count + entries.filter((document) => document.uploadId).length;
    }, 0);
    const fileCount = Math.max(uploadedFileCount, 1);
    const minimumExtractionDuration = fileCount * 2000;
    const extractionStartedAt = Date.now();
    const progressTimer = window.setInterval(() => {
      const elapsed = Date.now() - extractionStartedAt;
      const progress = Math.min(90, 4 + (elapsed / minimumExtractionDuration) * 86);
      setScanProgress(progress);
      setExtractionFileIndex(
        Math.min(fileCount - 1, Math.floor(elapsed / 2000)),
      );
    }, 100);
    try {
      const result = await dispatch(
        previewVendorProfileAction(buildProfilePayload(documentsToPreview)),
      );
      const data = result?.data || {};
      const previewDocuments = Array.isArray(data.documents)
        ? data.documents
        : Array.isArray(data.data?.documents)
          ? data.data.documents
          : [];
      if (!result?.success) {
        setExtractionStatus("failed");
        hasStartedPreviewRequest.current = false;
        setScanProgress(0);
        setScanError(
          result?.error ||
            data.message ||
            text("Unable to extract information from these documents."),
        );
        return;
      }

      const remainingExtractionTime =
        minimumExtractionDuration - (Date.now() - extractionStartedAt);
      if (remainingExtractionTime > 0) {
        await new Promise((resolve) =>
          window.setTimeout(resolve, remainingExtractionTime),
        );
      }

      const uploadedDocuments = DOCS.flatMap((config) => {
        const entries = config.multiple
          ? documentsToPreview[config.key] || []
          : [documentsToPreview[config.key]].filter(Boolean);
        return entries
          .filter((document) => document?.file || document?.uploadId)
          .map((document) => ({ config, document }));
      });
      const normalizedDocuments = previewDocuments.map((entry, index) => {
        const entryData = entry?.data && typeof entry.data === "object"
          ? entry.data
          : {};
        const entryKey = documentKeyForApiField(
          entry?.field ||
            entry?.key ||
            entry?.documentKey ||
            entry?.documentType ||
            entry?.type ||
            entryData.documentType ||
            entryData.type ||
            "",
        );
        const uploadId =
          entry?.uploadId || entry?.fileId || entry?.documentId || "";
        const source =
          uploadedDocuments.find(
            ({ document }) => uploadId && document.uploadId === uploadId,
          ) ||
          (entryKey
            ? uploadedDocuments.find(({ config }) => config.key === entryKey)
            : uploadedDocuments[index]);
        const rawApiLabel =
          entry?.documentLabel ||
          entry?.documentName ||
          entry?.label ||
          entry?.fileName ||
          entry?.name ||
          entry?.document?.label ||
          entry?.document?.name ||
          (typeof entry?.document === "string" ? entry.document : "") ||
          "";
        const apiLabel =
          typeof rawApiLabel === "string" || typeof rawApiLabel === "number"
            ? String(rawApiLabel)
            : rawApiLabel?.label || rawApiLabel?.name || "";
        return {
          ...entry,
          data: entryData,
          uploadedDocumentKey: source?.config?.key,
          uploadedDocumentId: source?.document?.uploadId || uploadId,
          label:
            apiLabel ||
            source?.config?.title ||
            source?.document?.name ||
            `${text("Document")} ${index + 1}`,
        };
      });

      setExtractedDocuments(normalizedDocuments);
      setExtractionStatus("passed");
      setScanProgress(100);
      setScanError("");
      saveDraft(3, form, documentsToPreview);
      await new Promise((resolve) => window.setTimeout(resolve, 350));
      setCurrentStep(3);
    } catch {
      setExtractionStatus("failed");
      hasStartedPreviewRequest.current = false;
      setScanProgress(0);
      setScanError(
        text("Unable to extract information from these documents."),
      );
    } finally {
      window.clearInterval(progressTimer);
      setIsExtracting(false);
    }
  }, [buildProfilePayload, dispatch, documents, form, isExtracting, saveDraft, text]);

  useEffect(() => {
    const ids = getDocumentIds(documents);
    const uploadedRequiredDocuments =
      Boolean(ids.registrationCertificate) &&
      Boolean(ids.taxCertificate) &&
      Boolean(ids.businessLicense);
    if (
      currentStep === 2 &&
      extractionStatus === "idle" &&
      !isExtracting &&
      uploadedRequiredDocuments
    ) {
      runProfilePreview(documents);
    }
  }, [
    currentStep,
    documents,
    extractionStatus,
    isExtracting,
    runProfilePreview,
  ]);

  const runDocumentVerification = async (documentsToVerify = documents) => {
    if (isScanning) return;
    hasStartedVerificationFlow.current = true;
    const payload = buildProfilePayload(documentsToVerify);
    setCurrentStep(4);
    setScanStatus("processing");
    setScanProgress(4);
    setScanResults([]);
    setFailedDocuments([]);
    setScanError("");
    setProfileSubmitError("");
    setIsScanning(true);

    const progressTimer = window.setInterval(
      () => setScanProgress((value) => Math.min(90, value + 3)),
      500,
    );
    try {
      const result = await dispatch(completeVendorProfileAction(payload));
      const data = result?.data || {};
      const results = Array.isArray(data.results)
        ? data.results
        : Array.isArray(data.verificationResults)
          ? data.verificationResults
          : Array.isArray(data.documents)
            ? data.documents
            : [];
      const reportedFailures = Array.isArray(data.failedDocuments)
        ? data.failedDocuments
        : [];
      const rejectedDocuments = reportedFailures.length
        ? reportedFailures
        : results.filter(isFailedVerificationEntry);
      const message =
        result?.error || data.message || data.error || "";

      setScanResults(results);
      setFailedDocuments(rejectedDocuments);
      setVerifiedVendor(
        data.vendor || data.data?.vendor || data.data?.data?.vendor || null,
      );

      if (
        result?.success &&
        data.verified !== false &&
        rejectedDocuments.length === 0
      ) {
        setScanStatus("passed");
        setScanProgress(100);
        setProfileSubmitError("");
        saveDraft(4, form, documentsToVerify);
        return;
      }

      setScanStatus("failed");
      const passedCount = results.filter((document) =>
        ["passed", "verified", "success", "successful"].includes(
          String(document.status || "").toLowerCase(),
        ),
      ).length;
      setScanProgress(
        results.length ? Math.round((passedCount / results.length) * 100) : 0,
      );
      setScanError(
        message || text("We could not verify these documents. Please try again."),
      );

      if (result?.status === 400 && /required/i.test(message)) {
        const failedField = documentKeyForApiMessage(message);
        const requiredKeys = failedField
          ? [failedField]
          : ["registrationCertificate", "taxCertificate", "businessLicense"];
        setDocumentErrors((value) => ({
          ...value,
          ...Object.fromEntries(requiredKeys.map((key) => [key, message])),
        }));
      }
    } catch {
      setScanStatus("failed");
      setScanProgress(0);
      setScanError(
        text("Verification is unavailable right now, please try again later."),
      );
    } finally {
      window.clearInterval(progressTimer);
      setIsScanning(false);
    }
  };

  const uploadDocumentsAndContinue = async () => {
    const missing = {};
    DOCS.filter((document) => document.required).forEach(({ key, multiple }) => {
      const hasDocument = multiple
        ? (documents[key] || []).some((document) => document.file || document.uploadId)
        : Boolean(documents[key]?.file || documents[key]?.uploadId);
      if (!hasDocument)
        missing[key] = text(
          "This document is required. Choose it again to continue.",
        );
    });
    setDocumentErrors(missing);
    if (Object.keys(missing).length) return;
    setIsUploading(true);
    const nextDocuments = {
      ...documents,
      otherCertificates: [...(documents.otherCertificates || [])],
    };
    try {
      for (const { key, multiple } of DOCS) {
        if (multiple) {
          const selectedList = [...(nextDocuments[key] || [])];
          for (let index = 0; index < selectedList.length; index += 1) {
            const selected = selectedList[index];
            if (!selected?.file || selected.uploadId) continue;
            const result = await dispatch(uploadVendorDocumentAction(selected.file));
            if (!result?.success) {
              nextDocuments[key] = selectedList;
              setDocuments(nextDocuments);
              saveDraft(1, form, nextDocuments);
              setDocumentErrors((value) => ({
                ...value,
                [key]: result?.message || text("Unable to upload this document."),
              }));
              return;
            }
            selectedList[index] = { ...selected, uploadId: result.uploadId };
          }
          nextDocuments[key] = selectedList;
          continue;
        }
        const selected = nextDocuments[key];
        if (!selected?.file || selected.uploadId) continue;
        const result = await dispatch(uploadVendorDocumentAction(selected.file));
        if (!result?.success) {
          setDocuments(nextDocuments);
          saveDraft(1, form, nextDocuments);
          setDocumentErrors((value) => ({
            ...value,
            [key]: result?.message || text("Unable to upload this document."),
          }));
          return;
        }
        nextDocuments[key] = { ...selected, uploadId: result.uploadId };
      }
      setDocuments(nextDocuments);
      saveDraft(2, form, nextDocuments);
      setDraftMessage("");
      setScanError("");
      hasStartedPreviewRequest.current = false;
      await runProfilePreview(nextDocuments);
    } finally {
      setIsUploading(false);
    }
  };

  const submitCompleteProfile = async () => {
    if (isSavingProfile) return;
    setProfileSubmitError("");
    setIsSavingProfile(true);
    const completion = {
      form,
      documents: serializeDocuments(documents),
      completedAt: new Date().toISOString(),
    };
    try {
      if (draftStorageKey) {
        localStorage.setItem(
          draftStorageKey,
          JSON.stringify({ step: 5, ...completion }),
        );
      }
      if (verifiedVendor) {
        dispatch({ type: AUTH_ME, payload: verifiedVendor });
      }
      setCurrentStep(5);
    } catch {
      setProfileSubmitError(
        text("We could not finish your profile. Please try again."),
      );
    } finally {
      setIsSavingProfile(false);
    }
  };
  const displayDocuments = DOCS.filter(
    ({ key, multiple }) =>
      multiple
        ? (documents[key] || []).some(
            (document) => document?.file || document?.name || document?.uploadId,
          )
        : documents[key]?.file ||
          documents[key]?.name ||
          documents[key]?.uploadId,
  );
  const uploadedScanFiles = DOCS.flatMap((config) => {
    const entries = config.multiple
      ? documents[config.key] || []
      : [documents[config.key]].filter(Boolean);
    return entries
      .filter((document) => document.file || document.name || document.uploadId)
      .map((document, index) => ({
        config,
        document,
        field: config.key,
        index,
        uploadId: document.uploadId || "",
        name: document.name || document.file?.name || text(config.title),
      }));
  });
  const viewUploadedDocument = (document) => {
    if (!document?.uploadId) return;
    window.open(
      `${backendUrl}/uploads/${encodeURIComponent(document.uploadId)}`,
      "_blank",
      "noopener,noreferrer",
    );
  };
  const reviewValue = (value) => value || text("Not provided");
  const extractedFields = [
    ["Document type", ["documentType", "document_type", "type"]],
    [
      "Company name",
      ["companyName", "company_name", "companyLegalName", "legalName"],
    ],
    [
      "Registration number",
      ["registrationNumber", "registration_number", "registrationNo"],
    ],
    ["Document number", ["documentNumber", "document_number", "documentNo"]],
    ["Country", ["country", "countryName", "country_name"]],
    ["Issued by", ["issuedBy", "issued_by", "issuer"]],
    ["Issue date", ["issueDate", "issue_date", "issuedDate"]],
    [
      "Expiry date",
      ["expiryDate", "expiry_date", "expirationDate", "expiresAt"],
    ],
  ];
  const uploadedDocumentKeys = new Set(displayDocuments.map(({ key }) => key));
  const reviewExtractedDocuments = extractedDocuments.flatMap((document) => {
    if (!uploadedDocumentKeys.has(document.uploadedDocumentKey)) return [];
    const fields = extractedFields.flatMap(([label, aliases]) => {
      const value = aliases
        .map((key) => document.data?.[key] ?? document[key])
        .find(
          (candidate) =>
            candidate !== undefined &&
            candidate !== null &&
            String(candidate).trim() !== "",
        );
      if (value === undefined) return [];
      const displayValue =
        typeof value === "object"
          ? value.name || value.label || JSON.stringify(value)
          : String(value);
      return [{ label, value: displayValue }];
    });
    return fields.length > 0 ? [{ document, fields }] : [];
  });
  const isProfileCompletePreview = currentStep === 5;
  const progressStep = getProgressStep(currentStep, scanStatus);
  const progressLabels = STEPS.map((label, index) => {
    if (index !== STEPS.length - 1) return label;
    if (currentStep === 4 && scanStatus === "failed") return "Error";
    if (currentStep === 5 && needsAdminApproval) return "Admin Approval";
    return label;
  });
  return (
    <main
      className={`complete-profile-page${currentStep === 2 || currentStep === 4 ? " complete-profile-page--scanning" : ""}`}
    >
      {!isProfileCompletePreview && (
        <header className="complete-profile-heading complete-profile-heading--with-back">
          <div className="complete-profile-heading-main">
            <button
              type="button"
              className="complete-profile-back-button"
              aria-label={text("Go back")}
              onClick={() => navigate(-1)}
            >
              <Icon name="back" />
            </button>
            <div className="complete-profile-heading-copy">
              <h1>{text("Complete your profile")}</h1>
              <p>
                {text(
                  "Add your business and contact details to unlock all platform features.",
                )}
              </p>
            </div>
          </div>
          <CompletionSummary step={progressStep} text={text} />
        </header>
      )}
      {!isProfileCompletePreview && (
        <StepProgress
          step={progressStep}
          text={text}
          steps={progressLabels}
          completeCurrentStep={currentStep === 5}
          canNavigateTo={(targetStep) =>
            !isScanning &&
            !isExtracting &&
            !isUploading
          }
          onStepClick={
            isCompletedProfilePreview
              ? undefined
              : (targetStep) => {
                  const flowStep = FLOW_STEP_BY_PROGRESS_STEP[targetStep];
                  setCurrentStep(flowStep);
                  saveDraft(flowStep, form, documents);
                }
          }
        />
      )}

      {currentStep === 0 && (
        <form className="complete-profile-card" onSubmit={handleCompanySubmit}>
          <SectionTitle
            icon="building"
            title={text("Company details")}
            description={text(
              "Provide your business information as it appears on official documents.",
            )}
          />
          <div className="row g-3 complete-profile-company-fields">
            <div className="col-12 col-md-6">
              <Field
                label={text("Company Legal Name")}
                value={form.companyLegalName}
                onChange={updateField("companyLegalName")}
                placeholder={text("Enter legal company name")}
                required
                error={formErrors.companyLegalName}
              />
            </div>
            <div className="col-12 col-md-6">
              <Field
                label={text("Trading Name")}
                value={form.tradingName}
                onChange={updateField("tradingName")}
                placeholder={text("Enter trading name (if different)")}
                required
                error={formErrors.tradingName}
              />
            </div>
            <div className="col-12 col-md-6">
              <Field
                label={text("Registration Number")}
                value={form.registrationNumber}
                onChange={updateField("registrationNumber")}
                placeholder={text("Enter registration number")}
                required
                error={formErrors.registrationNumber}
              />
            </div>
            <div className="col-12 col-md-6">
              <Field
                label={text("Country of Registration")}
                value={form.countryOfRegistration}
                onChange={updateField("countryOfRegistration")}
                placeholder={text("Select country")}
                options={countryOptions}
                required
                error={formErrors.countryOfRegistration}
              />
            </div>
            <div className="col-12 col-md-6">
              <Field
                label={text("Company Type")}
                value={form.companyType}
                onChange={updateField("companyType")}
                placeholder={text("Select company type")}
                options={companyTypeOptions}
                required
                error={formErrors.companyType}
              />
            </div>
          </div>
          {scanError && (
            <p className="complete-profile-scan-error" role="alert">
              {scanError}
            </p>
          )}
          <div className="complete-profile-actions complete-profile-company-actions">
            {draftMessage && (
              <span className="complete-profile-draft-message" role="status">
                {draftMessage}
              </span>
            )}
            <button type="submit" className="complete-profile-primary-button" disabled={!canContinueCompany}>
              {text("Continue")} <Icon name="arrow" />
            </button>
          </div>
        </form>
      )}

      {currentStep === 1 && (
        <section className="complete-profile-card">
          <SectionTitle
            icon="document"
            tone="green"
            title={text("Document upload")}
            description={text(
              "Upload the documents required to verify your business.",
            )}
          />
          <div className="row g-3 complete-profile-document-grid">
            {DOCS.map((document) => {
              const selected = document.multiple
                ? documents[document.key] || []
                : documents[document.key] || {};
              const selectedDocuments = document.multiple
                ? selected
                : selected.name || selected.uploadId
                  ? [selected]
                  : [];
              const selectedLabel = document.multiple
                ? selected.length
                  ? `${selected.length} ${text(
                      selected.length === 1
                        ? "certificate selected"
                        : "certificates selected",
                    )}`
                  : ""
                : selected.name;
              return (
                <div className="col-12 col-xl-6" key={document.key}>
                  <article className="complete-profile-document-card">
                    <div className="complete-profile-document-heading">
                      {/* <span
                        className={`complete-profile-document-icon ${document.tone}`}
                      >
                        <Icon name="document" />
                      </span> */}
                      <div>
                        <h3>
                          {sentenceCaseLabel(text(document.title))}
                          {document.required ? (
                            <b> *</b>
                          ) : (
                            <small> ({text("Optional")})</small>
                          )}
                        </h3>
                        {/* <p>{text(document.detail)}</p> */}
                      </div>
                    </div>
                    <label
                      className={`complete-profile-dropzone${selectedDocuments.length ? " has-file" : ""}`}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={(event) => handleDrop(event, document.key)}
                    >
                      <input
                        type="file"
                        accept=".pdf,application/pdf"
                        multiple={Boolean(document.multiple)}
                        onChange={(event) => {
                          setSelectedFiles(document.key, event.target.files);
                          event.target.value = "";
                        }}
                      />
                      <span className="complete-profile-drop-icon">
                        <Icon name="upload" />
                      </span>
                      <span className="complete-profile-drop-copy">
                        <strong>
                          {selectedLabel ||
                            text("Drag and drop your file here")}
                        </strong>
                        <span>
                          {selectedDocuments.length ? (
                            text(
                              document.multiple
                                ? "Add or replace files"
                                : "Click to replace file",
                            )
                          ) : (
                            <>
                              {text("or")} <b>{text("Browse Files")}</b>
                            </>
                          )}
                        </span>
                        <small>{text("PDF only (Max 10 MB)")}</small>
                      </span>
                      {selectedDocuments.length > 0 &&
                        selectedDocuments.every((item) => item.uploadId) && (
                        <span className="complete-profile-uploaded-tag">
                          <Icon name="check" /> {text("Uploaded")}
                        </span>
                        )}
                    </label>
                    {document.multiple && selectedDocuments.length > 0 && (
                      <div className="complete-profile-uploaded-files">
                        {selectedDocuments.map((item, index) => (
                          <div
                            className="complete-profile-uploaded-file"
                            key={`${item.uploadId || item.name}-${index}`}
                          >
                            <span>{item.name}</span>
                            {item.uploadId && (
                              <button
                                type="button"
                                className="complete-profile-view-button"
                                onClick={() => viewUploadedDocument(item)}
                              >
                                <Icon name="eye" /> {text("View")}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {!document.multiple && selected.uploadId && (
                      <button
                        type="button"
                        className="complete-profile-view-button"
                        onClick={() => viewUploadedDocument(selected)}
                      >
                        <Icon name="eye" /> {text("View")}
                      </button>
                    )}
                    {documentErrors[document.key] && (
                      <p
                        className="complete-profile-document-error"
                        role="alert"
                      >
                        {documentErrors[document.key]}
                      </p>
                    )}
                  </article>
                </div>
              );
            })}
          </div>
          {scanError && (
            <p className="complete-profile-scan-error" role="alert">
              {scanError}
            </p>
          )}
          <div className="complete-profile-actions">
            <button
              type="button"
              className="complete-profile-secondary-button"
              onClick={() => setCurrentStep(0)}
            >
              {text("Back")}
            </button>
            <button
              type="button"
              className="complete-profile-primary-button"
              disabled={isUploading || !requiredDocumentsReady}
              onClick={uploadDocumentsAndContinue}
            >
              {isUploading ? text("Uploading documents...") : text("Continue")}{" "}
              <Icon name="arrow" />
            </button>
          </div>
        </section>
      )}
      {currentStep === 3 && (
        <section className="complete-profile-review-stack">
          <article className="complete-profile-card">
            <SectionTitle
              icon="building"
              title={text("Company details")}
              description={text(
                "Review your business information as it appears on official documents.",
              )}
            />
            <div className="complete-profile-review-grid">
              <ReviewField
                label={text("Company Legal Name")}
                value={reviewValue(form.companyLegalName)}
              />
              <ReviewField
                label={text("Trading Name")}
                value={reviewValue(form.tradingName)}
              />
              <ReviewField
                label={text("Registration Number")}
                value={reviewValue(form.registrationNumber)}
              />
              <ReviewField
                label={text("Country of Registration")}
                value={reviewValue(form.countryOfRegistration)}
              />
              <ReviewField
                label={text("Company Type")}
                value={reviewValue(form.companyType)}
              />
            </div>
          </article>
          <article className="complete-profile-card">
            <SectionTitle
              icon="document"
              tone="green"
              title={text("Documents")}
              description={text(
                "Review the uploaded documents required for verification.",
              )}
            />
            <div className="complete-profile-review-documents">
              {displayDocuments.map((document) => (
                <div
                  className="complete-profile-review-document"
                  key={document.key}
                >
                  <span
                    className={`complete-profile-document-icon ${document.tone}`}
                  >
                    <Icon name="document" />
                  </span>
                  <strong>{text(document.title)}</strong>
                  <span className="complete-profile-file-name">
                    {document.multiple
                      ? (documents[document.key] || [])
                          .filter(
                            (item) => item?.file || item?.name || item?.uploadId,
                          )
                          .map(
                            (item) =>
                              item.name || item.file?.name || text(document.title),
                          )
                          .join(", ")
                      : documents[document.key]?.name ||
                        documents[document.key]?.file?.name ||
                        (documents[document.key]?.uploadId
                          ? text(document.title)
                          : "")}
                  </span>
                  {document.multiple ? (
                    (documents[document.key] || [])
                      .filter(
                        (item) => item?.file || item?.name || item?.uploadId,
                      )
                      .map(
                        (item, index) =>
                          item.uploadId && (
                            <button
                              type="button"
                              className="complete-profile-view-button"
                              key={`${item.uploadId}-${index}`}
                              onClick={() => viewUploadedDocument(item)}
                            >
                              <Icon name="eye" /> {text("View")} {index + 1}
                            </button>
                          ),
                      )
                  ) : (
                    documents[document.key]?.uploadId && (
                    <button
                      type="button"
                      className="complete-profile-view-button"
                      onClick={() =>
                        viewUploadedDocument(documents[document.key])
                      }
                    >
                      <Icon name="eye" /> {text("View")}
                    </button>
                    )
                  )}
                </div>
              ))}
            </div>
          </article>
          {profileSubmitError && (
            <p className="complete-profile-scan-error" role="alert">
              {profileSubmitError}
            </p>
          )}
          <div className="complete-profile-actions complete-profile-actions--plain">
            <button
              type="button"
              className="complete-profile-secondary-button"
              onClick={() => {
                if (isCompletedProfilePreview) {
                  setIsCompletedProfilePreview(false);
                  setCurrentStep(5);
                } else {
                  setCurrentStep(1);
                }
              }}
            >
              {isCompletedProfilePreview
                ? text("Back to success")
                : text("Back")}
            </button>
            {!isCompletedProfilePreview && (
              <button
                type="button"
                className="complete-profile-primary-button"
                disabled={isScanning}
                onClick={() => runDocumentVerification(documents)}
              >
                {isScanning
                  ? text("Starting verification...")
                  : text("Confirm and verify documents")}{" "}
                <Icon name="arrow" />
              </button>
            )}
          </div>
        </section>
      )}

      {(currentStep === 2 || currentStep === 4) &&
        (() => {
          const isVerificationStep = currentStep === 4;
          const currentScanStatus = isVerificationStep
            ? scanStatus
            : extractionStatus;
          const currentScanIsActive = isVerificationStep
            ? isScanning
            : isExtracting;
          const scanStages = isVerificationStep
            ? [
                [text("Checking document integrity"), 15],
                [text("Matching company information"), 36],
                [text("Validating document details"), 58],
                [text("Cross-checking verification data"), 82],
                [text("Finalizing verification"), 100],
              ]
            : [
                [text("Reading document content"), 15],
                [text("Extracting key information"), 36],
                [text("Organizing extracted details"), 58],
                [text("Preparing information for review"), 82],
                [text("Finalizing extracted details"), 100],
              ];
          const verificationChecks = [
            [
              text("Checking with database records"),
              text("Validating company information..."),
              "document",
            ],
            [
              text("Comparing document details"),
              text("Matching key information..."),
              "document",
            ],
            [
              text("Validating with entered information"),
              text("Analysing details..."),
              "shield",
            ],
          ];
          const activeStageIndex = scanStages.findIndex(
            ([, threshold]) => scanProgress < threshold,
          );
          const hasDocumentFailures =
            isVerificationStep && failedVerificationEntries.length > 0;
          const aiScreenState = currentScanIsActive
            ? "scanning"
            : currentScanStatus === "passed"
              ? "success"
              : currentScanStatus === "failed"
                ? "error"
                : "idle";
          const isAdminApprovalError =
            isVerificationStep &&
            aiScreenState === "error" &&
            hasDocumentFailures;
          return (
            <section
              className={`complete-profile-scan-page is-${aiScreenState}${
                isVerificationStep ? " is-verification" : ""
              }${isVerificationStep && hasDocumentFailures ? " has-failures" : ""}`}
            >
              <div
                className={
                  "complete-profile-scan-content" +
                  (isAdminApprovalError ? " is-error-layout" : "")
                }
              >
              {isVerificationStep &&
                aiScreenState === "error" &&
                hasDocumentFailures && (
                  <div className="complete-profile-error-visual" aria-hidden="true">
                    <span className="complete-profile-error-icon">!</span>
                  </div>
                )}
              <header className="complete-profile-scan-heading">
                <h1>
                  {sentenceCaseLabel(text(
                    aiScreenState === "success"
                      ? "All documents verified successfully!"
                      : aiScreenState === "error" && isVerificationStep
                        ? hasDocumentFailures
                          ? "Some documents need attention"
                          : "Verification could not be completed"
                        : aiScreenState === "error"
                          ? "Document information could not be extracted"
                          : isVerificationStep
                            ? "AI Verification in progress"
                            : "AI is reading your documents",
                  ))}
                </h1>
                <p>
                  {text(
                    aiScreenState === "success"
                      ? "All submitted documents passed verification. Continue to complete your profile."
                      : aiScreenState === "error" && isVerificationStep
                        ? hasDocumentFailures
                            ? "Admin review is required for the documents listed below."
                          : "The verification service could not complete the scan. You can retry verification."
                        : aiScreenState === "error"
                          ? scanError || "We could not extract information from these documents. You can retry extraction."
                          : isVerificationStep
                            ? "We are checking your documents, database information and the details you have entered."
                            : "Our AI is extracting important information from your uploaded documents. This will only take a few moments.",
                  )}
                </p>
              </header>
              {(aiScreenState === "scanning" || aiScreenState === "idle") && (
              <div className="complete-profile-ai-visual" aria-hidden="true">
                {!isVerificationStep && (
                  <>
                    <span className="complete-profile-ai-beam complete-profile-ai-beam--excel" />
                    <span className="complete-profile-ai-beam complete-profile-ai-beam--word" />
                    <span className="complete-profile-ai-beam complete-profile-ai-beam--pdf" />
                    <span className="complete-profile-ai-beam complete-profile-ai-beam--image" />
                    <div className="complete-profile-ai-file complete-profile-ai-file--excel">
                      <span className="complete-profile-ai-file-mark">X</span>
                      <small>XLS</small>
                    </div>
                    <div className="complete-profile-ai-file complete-profile-ai-file--word">
                      <span className="complete-profile-ai-file-mark">W</span>
                      <small>DOC</small>
                    </div>
                    <div className="complete-profile-ai-file complete-profile-ai-file--pdf">
                      <span className="complete-profile-ai-file-mark">PDF</span>
                      <small>PDF</small>
                    </div>
                    <div className="complete-profile-ai-file complete-profile-ai-file--image">
                      <span className="complete-profile-ai-image-mark"><i /></span>
                      <small>IMAGE</small>
                    </div>
                  </>
                )}
                <span className="complete-profile-ai-radar complete-profile-ai-radar--outer" />
                <span className="complete-profile-ai-radar complete-profile-ai-radar--inner" />
                <span className="complete-profile-ai-particle complete-profile-ai-particle--one" />
                <span className="complete-profile-ai-particle complete-profile-ai-particle--two" />
                <span className="complete-profile-ai-particle complete-profile-ai-particle--three" />
                <span className="complete-profile-ai-particle complete-profile-ai-particle--four" />
                <span className="complete-profile-ai-particle complete-profile-ai-particle--five" />
                <span className="complete-profile-ai-particle complete-profile-ai-particle--six" />
                <div className="complete-profile-ai-frame">
                  <span className="complete-profile-ai-corner complete-profile-ai-corner--top-left" />
                  <span className="complete-profile-ai-corner complete-profile-ai-corner--top-right" />
                  <span className="complete-profile-ai-corner complete-profile-ai-corner--bottom-left" />
                  <span className="complete-profile-ai-corner complete-profile-ai-corner--bottom-right" />
                  <div className="complete-profile-ai-document">
                    <span className="complete-profile-ai-pdf-label">PDF</span>
                    <span className="complete-profile-ai-document-line" />
                    <span className="complete-profile-ai-document-line" />
                    <span className="complete-profile-ai-document-line" />
                    <span className="complete-profile-ai-document-line" />
                    <span className="complete-profile-ai-document-line" />
                    <span className="complete-profile-ai-document-picture">
                      <i />
                    </span>
                  </div>
                </div>
                <span className="complete-profile-ai-scan-line" />
              </div>
              )}

              {(aiScreenState === "scanning" || aiScreenState === "idle") && (
                <article
                  className={
                    "complete-profile-scan-processing-card" +
                    (isVerificationStep ? " is-verification" : "")
                  }
                >
                  {isVerificationStep ? (
                    <>
                      {uploadedScanFiles.length > 0 && (
                        <div className="complete-profile-scanned-file-names">
                          <strong>{text("Uploaded files being scanned")}</strong>
                          <span title={uploadedScanFiles.map((file) => file.name).join(", ")}>
                            {uploadedScanFiles.map((file) => file.name).join(" · ")}
                          </span>
                        </div>
                      )}
                      <div
                        className="complete-profile-verification-checks"
                        aria-live="polite"
                      >
                        {verificationChecks.map(([title, detail, icon]) => (
                          <div
                            className="complete-profile-verification-check"
                            key={title}
                          >
                            <span className="complete-profile-verification-check-icon">
                              <Icon name={icon} />
                            </span>
                            <span className="complete-profile-verification-check-copy">
                              <strong>{title}</strong>
                              <small>{detail}</small>
                            </span>
                            <span
                              className="complete-profile-verification-check-spinner"
                              role="status"
                              aria-label={text("Scanning")}
                            />
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="complete-profile-scan-processing-header">
                        <span className="complete-profile-scan-processing-icon">
                          <Icon name="settings" />
                        </span>
                        <strong>
                          {text(
                            currentScanIsActive
                              ? uploadedScanFiles.length > 0
                                ? `${text("Reading document")} ${Math.min(
                                    extractionFileIndex + 1,
                                    uploadedScanFiles.length,
                                  )} ${text("of")} ${uploadedScanFiles.length}...`
                                : "Reading and extracting information..."
                              : currentScanStatus === "idle"
                                ? "Preparing document extraction"
                                : "Extraction needs attention",
                          )}
                        </strong>
                        <b>{scanProgress}%</b>
                      </div>
                      <div
                        className="complete-profile-scan-processing-track"
                        role="progressbar"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        aria-valuenow={scanProgress}
                        aria-label={text("AI scan progress")}
                      >
                        <span style={{ width: scanProgress + "%" }} />
                      </div>
                      <div
                        className="complete-profile-scan-stages"
                        aria-live="polite"
                      >
                        {scanStages.map(([label, threshold], index) => {
                          const isDone = scanProgress >= threshold;
                          const isActive = index === activeStageIndex;
                          const state = isDone
                            ? "is-done"
                            : isActive
                              ? "is-active"
                              : "is-upcoming";
                          return (
                            <div
                              className={"complete-profile-scan-stage " + state}
                              key={label}
                            >
                              <span className="complete-profile-scan-stage-marker">
                                {isDone ? (
                                  <Icon name="check" />
                                ) : isActive ? (
                                  <i />
                                ) : null}
                              </span>
                              <span>{label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </article>
              )}

              {aiScreenState === "success" && (
                <>
                  <div className="complete-profile-ai-result-hero is-success" role="status">
                    <span><Icon name="check" /></span>
                    <div className="complete-profile-ai-result-copy">
                      <strong>{text("AI verification successful")}</strong>
                      <p>{text("All submitted documents passed verification.")}</p>
                    </div>
                  </div>
                </>
              )}

              {aiScreenState === "error" && !hasDocumentFailures && (
                <div className="complete-profile-ai-result-hero is-error" role="alert">
                  <span>!</span>
                  <div className="complete-profile-ai-result-copy">
                    <strong>
                      {text(
                        isVerificationStep
                          ? "Verification needs attention"
                          : "Document extraction failed",
                      )}
                    </strong>
                    <p>
                      {text(
                        hasDocumentFailures
                          ? needsAdminApproval
                            ? "Failed documents require Admin approval before you can continue."
                            : "Admin approval has been granted. You can continue to complete your profile."
                          : scanError || "Please try the verification again.",
                      )}
                    </p>
                  </div>
                </div>
              )}

              {isVerificationStep && hasDocumentFailures && (
                <article className="complete-profile-failed-documents">
                  <div className="complete-profile-failed-document-list">
                  {failedVerificationEntries.map((entry, index) => {
                    const fieldKey = documentKeyForApiField(
                      entry.field ||
                        entry.key ||
                        entry.documentKey ||
                        entry.documentType ||
                        entry.label ||
                        "",
                    );
                    const matchingDocument = DOCS.find(
                      (document) => document.key === fieldKey,
                    );
                    const entryUploadId =
                      entry.uploadId || entry.fileId || entry.documentId || "";
                    const matchingFile =
                      uploadedScanFiles.find(
                        (file) =>
                          entryUploadId && file.uploadId === entryUploadId,
                      ) ||
                      uploadedScanFiles.find(
                        (file) =>
                          file.field === fieldKey &&
                          (entry.index === undefined ||
                            file.index === entry.index),
                      ) ||
                      uploadedScanFiles.find(
                        (file) =>
                          entry.fileName &&
                          file.name.toLowerCase() ===
                            String(entry.fileName).toLowerCase(),
                      );
                    const rawLabel =
                      entry.documentLabel ||
                      entry.fileName ||
                      entry.documentName ||
                      entry.name ||
                      entry.label ||
                      entry.document?.label ||
                      entry.document?.name ||
                      "";
                    const label =
                      typeof rawLabel === "string" ||
                      typeof rawLabel === "number"
                        ? String(rawLabel)
                        : rawLabel?.label ||
                          rawLabel?.name ||
                          (matchingDocument
                            ? text(matchingDocument.title)
                            : `${text("Document")} ${index + 1}`);
                    const rawReason =
                      entry.reason ||
                      entry.failureReason ||
                      entry.failure_reason ||
                      entry.verificationReason ||
                      entry.errorMessage ||
                      entry.error ||
                      entry.verificationError ||
                      entry.message ||
                      entry.validationMessage ||
                      entry.verificationResult ||
                      entry.result ||
                      "";
                    const reason =
                      typeof rawReason === "string"
                        ? rawReason
                        : rawReason?.message || JSON.stringify(rawReason);
                    return (
                      <details
                        className="complete-profile-failed-document"
                        open
                        key={entry.uploadId || entry.documentId || `${label}-${index}`}
                      >
                        <summary>
                          <span className="complete-profile-failed-document-copy">
                            <strong>
                              {matchingDocument
                                ? text(matchingDocument.title)
                                : label}
                            </strong>
                            {matchingFile?.name && (
                              <small>{matchingFile.name}</small>
                            )}
                          </span>
                          <span className="complete-profile-failed-document-toggle">
                            <span className="complete-profile-failed-status">
                              {text("Failed")}
                            </span>
                            <Icon name="chevronDown" />
                          </span>
                        </summary>
                        {reason && <p>{String(reason)}</p>}
                      </details>
                    );
                  })}
                  </div>
                </article>
              )}

              {profileSubmitError && (
                <p className="complete-profile-scan-error" role="alert">
                  {profileSubmitError}
                </p>
              )}

              {aiScreenState === "error" && hasDocumentFailures && (
                <article className="complete-profile-admin-approval-panel">
                  <span className="complete-profile-admin-approval-icon">
                    <Icon name="shield" />
                  </span>
                  <div className="complete-profile-admin-approval-copy">
                    <strong>
                      {text(
                        needsAdminApproval
                          ? "Admin review"
                          : "Admin approval granted",
                      )}
                    </strong>
                    <p>
                      {text(
                        needsAdminApproval
                          ? "An Admin will review these failed documents for approval. You will receive an email with the decision."
                          : "Admin approval is complete. You can continue to complete your profile.",
                      )}
                    </p>
                  </div>
                </article>
              )}
              </div>

              <div className="complete-profile-actions complete-profile-actions--plain complete-profile-scan-actions">
                <button
                  type="button"
                  className="complete-profile-secondary-button"
                  onClick={() => setCurrentStep(isVerificationStep ? 3 : 1)}
                  disabled={currentScanIsActive}
                >
                  {text(isVerificationStep ? "Back to review" : "Back")}
                </button>
                {isVerificationStep && aiScreenState === "error" ? (
                  <button
                    type="button"
                    className="complete-profile-primary-button"
                    onClick={() => navigate("/dashboard")}
                  >
                    {text("Go to dashboard")} <Icon name="arrow" />
                  </button>
                ) : isVerificationStep &&
                (scanStatus === "passed" ||
                  hasDocumentFailures ||
                  allFailedDocumentsApproved) ? (
                  <button
                    type="button"
                    className="complete-profile-primary-button"
                    disabled={isScanning || isSavingProfile}
                    onClick={() => submitCompleteProfile()}
                  >
                    {text(
                      isSavingProfile
                        ? "Finishing..."
                        : "Complete Profile",
                    )} <Icon name="arrow" />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="complete-profile-primary-button"
                    disabled={
                      currentScanIsActive ||
                      !["failed", "idle"].includes(currentScanStatus)
                    }
                    onClick={() =>
                      isVerificationStep
                        ? runDocumentVerification(documents)
                        : runProfilePreview(documents)
                    }
                  >
                    {currentScanIsActive
                      ? text(isVerificationStep ? "Verifying..." : "Extracting...")
                      : text(
                          isVerificationStep
                            ? "Retry verification"
                            : "Retry extraction",
                        )} <Icon name="arrow" />
                  </button>
                )}
              </div>
            </section>
          );
        })()}
      {currentStep === 5 && (
        <section className="complete-profile-success-page">
          <header className="complete-profile-heading">
            <h1>
              {text(
                needsAdminApproval
                  ? "Profile submitted for admin approval"
                  : "Profile completed successfully",
              )}
            </h1>
            <p>
              {text(
                needsAdminApproval
                  ? "Your documents have been sent for manual review. You will receive an email when the review is complete."
                  : "Your vendor profile has been verified and all features are now unlocked.",
              )}
            </p>
          </header>
          <StepProgress
            step={4}
            completeCurrentStep={!needsAdminApproval}
            text={text}
            steps={progressLabels}
            canNavigateTo={(targetStep) => targetStep === 2}
            onStepClick={(targetStep) => {
              if (targetStep !== 2) return;
              setIsCompletedProfilePreview(true);
              setCurrentStep(3);
            }}
          />
          <article className="complete-profile-success-hero">
            <div className="complete-profile-success-art" aria-hidden="true">
              <span className="complete-profile-success-rays" />
              <span className="complete-profile-success-check">
                <Icon name="check" />
              </span>
              <i className="complete-profile-confetti confetti-one" />
              <i className="complete-profile-confetti confetti-two" />
              <i className="complete-profile-confetti confetti-three" />
              <i className="complete-profile-confetti confetti-four" />
            </div>
            <div>
              <h2>
                {text(
                  needsAdminApproval
                    ? "Your profile is awaiting admin review."
                    : "Your vendor profile is ready!",
                )}
              </h2>
              <p>
                {text(
                  needsAdminApproval
                    ? "Your business details and documents were submitted. Features that require verification will be available after admin approval."
                    : "Your business details have been saved and your documents have been uploaded. You can now use the vendor features available on LUALI Commodities.",
                )}
              </p>
            </div>
          </article>
          <div className="complete-profile-success-cards">
            <SuccessCard
              icon="building"
              title={text("Company details added")}
              description={text(
                "Your business information has been successfully submitted.",
              )}
            />
            <SuccessCard
              icon="document"
              title={text("Documents uploaded")}
              description={text(
                "Your required business documents are ready for review.",
              )}
              tone="green"
            />
            <SuccessCard
              icon="shield"
              title={text(
                needsAdminApproval
                  ? "Admin approval pending"
                  : "AI verification completed",
              )}
              description={text(
                needsAdminApproval
                  ? "The failed documents are awaiting manual review."
                  : "Your profile review has been completed.",
              )}
              tone="blue"
            />
          </div>
          <article className="complete-profile-next-card">
            <h2>{text("What you can do next")}</h2>
            <p>
              {text(
                needsAdminApproval
                  ? "Your profile features will be available after the manual document review."
                  : "Start using all the features available to grow your business on LUALI Commodities.",
              )}
            </p>
            <div className="complete-profile-next-grid">
              <NextAction
                icon="building"
                title={text("Create product listing")}
                description={text("Add and manage your commodity products.")}
                onClick={() => navigate("/add-product")}
              />
              <NextAction
                icon="document"
                title={text("Track orders")}
                description={text(
                  "Check the status of your operations and transactions.",
                )}
                onClick={() => navigate("/order")}
                tone="blue"
              />
              <NextAction
                icon="scan"
                title={text("View your profile")}
                description={text("Review your vendor information.")}
                onClick={() => navigate("/setting?tab=profile")}
                tone="green"
              />
            </div>
          </article>
          <div className="complete-profile-success-actions">
            <button
              type="button"
              className="complete-profile-primary-button"
              onClick={() => navigate("/dashboard")}
            >
              {text("Go to dashboard")} <Icon name="arrow" />
            </button>
            <button
              type="button"
              className="complete-profile-secondary-button"
              onClick={() => navigate("/setting?tab=profile")}
            >
              {text("View profile")}
            </button>
          </div>
        </section>
      )}
    </main>
  );
}

export default CompleteProfile;
